import React, { useEffect, useRef, useState } from 'react';
import { FileText, X } from 'lucide-react';
import { LeadMagnetCapture, MAGNETS } from '../home/LeadMagnetCapture';
import { TOKENS, limeBtn, displayFont } from '../../lib/designTokens';

const DISMISSED_KEY = 'nin_soft_gate_dismissed_v1'; // permanent X dismiss — covers all stages
const SUPPRESSION_MS = 30 * 24 * 60 * 60 * 1000;    // 30 days
const REVEAL_SCROLL_PX = 600;

// Per-magnet submitted-at key. Stage 0 ('peptide_cheatsheet') keeps the legacy
// key for backwards compat with the prior PR; stage 1+ uses a generic key.
const submittedKeyFor = (magnetId) =>
  magnetId === 'peptide_cheatsheet'
    ? 'nin_soft_gate_submitted_at_v1'
    : `nin_soft_gate_${magnetId}_submitted_at_v1`;

// CRO Audit §3.3 — complementary "soft" lead-magnet surface.
// Sticky pill bottom-right that reveals after the user scrolls REVEAL_SCROLL_PX.
//
// Auto-rotation logic (driven by MAGNETS order):
//   • Stage 0 (cheatsheet) never submitted  → show stage 0
//   • Stage 0 submitted < 30d ago            → HIDE pill (current stage still in suppression)
//   • Stage 0 submitted ≥ 30d ago, stage 1 never submitted → show stage 1
//   • Stage 1 submitted < 30d ago            → HIDE pill
//   • All stages submitted ≥ 30d ago         → HIDE pill (user already has everything)
//
// Manual X dismiss     → permanent across ALL stages (DISMISSED_KEY = '1')
// Modal close w/o submit → no suppression (pill re-appears when scroll threshold re-hit)
export const SoftLeadMagnetGate = () => {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeMagnet, setActiveMagnet] = useState(null);
  // Persisted-dismiss flag mirrored into a ref so the scroll handler never
  // reads stale state.
  const dismissedRef = useRef(false);

  useEffect(() => {
    let chosen = null;
    try {
      if (localStorage.getItem(DISMISSED_KEY) === '1') {
        dismissedRef.current = true;
        return undefined;
      }
      // Walk stages in order; first stage with no submitted_at OR expired
      // suppression becomes active. If a stage is mid-suppression, hide.
      for (const magnet of MAGNETS) {
        const ts = localStorage.getItem(submittedKeyFor(magnet.id));
        if (!ts) { chosen = magnet; break; }
        const age = Date.now() - new Date(ts).getTime();
        if (!Number.isFinite(age) || age < 0) { chosen = magnet; break; }
        if (age < SUPPRESSION_MS) {
          // Current stage is mid-suppression — don't show anything yet.
          dismissedRef.current = true;
          return undefined;
        }
        // age ≥ SUPPRESSION_MS → expired, move on to next stage.
      }
    } catch (err) {
      console.error('Soft-gate storage read failed:', err);
    }

    if (!chosen) {
      // No more stages to offer (every magnet submitted > 30d ago).
      dismissedRef.current = true;
      return undefined;
    }
    setActiveMagnet(chosen);

    const onScroll = () => {
      if (dismissedRef.current) return;
      if (window.scrollY >= REVEAL_SCROLL_PX) {
        setVisible(true);
        window.removeEventListener('scroll', onScroll);
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // Run once in case the page loads already-scrolled (back-navigation).
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const dismissPill = () => {
    dismissedRef.current = true;
    try { localStorage.setItem(DISMISSED_KEY, '1'); }
    catch (err) { console.error('Soft-gate storage write failed:', err); }
    setVisible(false);
  };

  // Wired into LeadMagnetCapture's onSuccess so a successful PDF delivery
  // earns the user a 30-day suppression window for THAT magnet only — the
  // next stage will surface in 30 days.
  const handleSubmitSuccess = ({ magnet_id } = {}) => {
    const id = magnet_id || activeMagnet?.id;
    if (!id) return;
    dismissedRef.current = true;
    try { localStorage.setItem(submittedKeyFor(id), new Date().toISOString()); }
    catch (err) { console.error('Soft-gate submit-time write failed:', err); }
    // Keep modal open so the user can see the success state + PDF download link.
    // Only hide the pill behind it.
    setVisible(false);
  };

  const closeModal = () => setOpen(false);

  if (!activeMagnet) return null;

  return (
    <>
      {/* Sticky pill — bottom-right (only when not currently in modal) */}
      {visible && !open && (
        <div
          className="fixed bottom-20 right-4 md:bottom-20 md:right-6 z-[90] flex items-stretch shadow-2xl animate-fade-in"
          style={{ boxShadow: '0 10px 32px rgba(199,250,62,0.30)' }}
          data-testid="soft-lead-magnet-container"
        >
          <button
            onClick={() => setOpen(true)}
            className="flex items-center gap-2 pl-4 pr-3 py-3 transition-transform hover:scale-[1.03] active:scale-[0.97]"
            style={limeBtn}
            data-testid="soft-lead-magnet-pill"
            data-magnet-id={activeMagnet.id}
            aria-label={`Open ${activeMagnet.pillLabel || activeMagnet.headline}`}
          >
            <FileText className="w-4 h-4" />
            <span
              className="text-xs md:text-sm uppercase tracking-wider"
              style={displayFont}
            >
              {activeMagnet.pillLabel || activeMagnet.headline}
            </span>
            <span aria-hidden="true" className="text-base leading-none">→</span>
          </button>
          <button
            onClick={dismissPill}
            className="flex items-center justify-center w-8 transition-colors hover:bg-black/15 border-l border-black/15"
            style={{ ...limeBtn, color: '#0B0B0C' }}
            data-testid="soft-lead-magnet-dismiss"
            aria-label={`Dismiss ${activeMagnet.pillLabel || activeMagnet.headline} offer`}
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* Modal — same look as ExitIntentModal but opt-in click instead of exit-intent */}
      {open && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
          data-testid="soft-lead-magnet-modal"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative w-full max-w-2xl animate-fade-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeModal}
              className="absolute -top-3 -right-3 z-10 w-9 h-9 flex items-center justify-center rounded-full"
              style={{ backgroundColor: TOKENS.surface2, border: '1px solid rgba(255,255,255,0.1)' }}
              data-testid="soft-lead-magnet-close"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-zinc-300" />
            </button>
            <LeadMagnetCapture magnet={activeMagnet} variant="modal" onSuccess={handleSubmitSuccess} />
          </div>
        </div>
      )}
    </>
  );
};
